"use client"

import { useEffect, useState } from "react"
import { CRASH_REPORT_EVENT, type CrashReport } from "@/components/error-capture"
import { getFirstChunkUrl } from "@/lib/crash-locator"

/** Development-only overlay: shows suspect file:line:col, document ID, and .map link. */
export function CrashLocatorOverlay() {
  const [report, setReport] = useState<CrashReport | null>(null)

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return

    const handler = (e: CustomEvent<CrashReport>) => {
      setReport(e.detail)
    }

    window.addEventListener(CRASH_REPORT_EVENT, handler as EventListener)
    return () => window.removeEventListener(CRASH_REPORT_EVENT, handler as EventListener)
  }, [])

  if (process.env.NODE_ENV !== "development" || !report) return null

  const suspect = report.suspect
  const suspectFormatted = report.suspectFormatted ?? "(no frame parsed)"
  const chunkUrl = getFirstChunkUrl(report.stack)
  const mapUrl = chunkUrl ? `${chunkUrl}.map` : null

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed bottom-4 left-4 right-4 z-[99999] max-w-2xl rounded-lg border-2 border-red-500/80 bg-red-950/95 p-4 font-mono text-sm text-red-100 shadow-lg backdrop-blur sm:left-4 sm:right-auto"
    >
      <div className="mb-2 font-semibold text-red-200">[Crash Locator] Development overlay</div>
      <div className="space-y-1 break-all">
        <div>
          <span className="text-red-300">Error: </span>
          {report.message}
        </div>
        <div>
          <span className="text-red-300">Route: </span>
          {report.route}
        </div>
        <div>
          <span className="text-red-300">Suspect file:line:col: </span>
          <strong className="text-white">{suspectFormatted}</strong>
        </div>
        {report.documentId && (
          <div>
            <span className="text-red-300">Firestore doc: </span>
            <code className="text-white">debug_crashes/{report.documentId}</code>
          </div>
        )}
        {suspect && (
          <div className="mt-2 rounded bg-black/30 p-2 text-xs">
            <div className="text-red-300">First app frame (raw):</div>
            <div className="break-all text-white">{suspect.raw}</div>
          </div>
        )}
        {mapUrl && (
          <div className="mt-2">
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-300 underline hover:text-primary-200"
            >
              Open .map for crashing chunk
            </a>
          </div>
        )}
        {report.sourcemapWarn && (
          <div className="mt-2 rounded border border-amber-500/50 bg-amber-950/50 p-2 text-amber-200">
            ⚠ Sourcemap may not be applied — stack has no app/lib/components paths. Check .map serving.
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => setReport(null)}
        className="mt-3 rounded bg-red-800 px-3 py-1 text-red-100 hover:bg-red-700"
      >
        Dismiss
      </button>
    </div>
  )
}
